import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, CheckCircle } from 'lucide-react'
import { WaveIcon } from '@/components/common/WaveIcon'
import { supabase } from '@/lib/supabase'

export const ResetPassword = () => {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  // Supabase puts the access token in the URL hash on redirect
  // The Supabase client picks it up automatically — we just need to call updateUser
  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Session is now set — user can update password
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    setIsLoading(false)

    if (error) {
      setError(error.message)
    } else {
      setDone(true)
      setTimeout(() => navigate('/'), 2500)
    }
  }

  return (
    <div className="min-h-screen bg-[#02182B] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-10 h-10 bg-[#FD802E] rounded-lg flex items-center justify-center">
            <WaveIcon size="md" />
          </div>
        </div>

        {done ? (
          <div className="bg-[#233D4C] border border-green-500/30 rounded-xl p-8 text-center space-y-3">
            <CheckCircle size={32} className="text-green-400 mx-auto" />
            <h2 className="text-white text-xl font-semibold">Password updated</h2>
            <p className="text-gray-400 text-sm">Redirecting you to the dashboard…</p>
          </div>
        ) : (
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl p-8 space-y-6">
            <div className="text-center">
              <h2 className="text-white text-xl font-semibold">Set new password</h2>
              <p className="text-gray-400 text-sm mt-1">Choose a strong password for your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-gray-400 text-sm">New password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Min 8 characters"
                    className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] focus:ring-1 focus:ring-[#FD802E]/30 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-gray-400 text-sm">Confirm password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="Repeat your password"
                  className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] focus:ring-1 focus:ring-[#FD802E]/30"
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !password || !confirm}
                className="w-full bg-[#FD802E] text-[#233D4C] font-semibold py-3 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
